import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateMailing } from '@enterprise/api';
import { ensureError } from '@enterprise/core';

import type { MutationCallbacks } from '@/types/common/mutation-callbacks.type';

import { datastreamQueryKeys } from '@/common/query-keys';
import { useApplicationStore } from '@hooks/application/application-store.hook';

import type { UpsertMailingArguments } from './types/upsert-mailing-arguments.type';

export const useUpdateMailing = (options?: MutationCallbacks) => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  const queryClient = useQueryClient();
  const toast = useToast();

  const { onError, onSuccess } = options ?? {};

  return useMutation({
    mutationFn: async ({ mailing, mailingId }: UpsertMailingArguments) => {
      return await updateMailing(enterpriseApi, { ...mailing, id: mailingId });
    },
    onError: (e) => {
      toast({
        description: ensureError(e).message,
        duration: 4000,
        isClosable: true,
        status: 'error',
        title: 'Failed to update mailing settings',
      });

      onError?.();
    },
    onSuccess: (_, { mailingId }) => {
      toast({
        description: 'Mailing settings updated successfully',
        isClosable: true,
        status: 'success',
        title: 'Mailing Settings Updated',
      });

      void queryClient.invalidateQueries({ queryKey: datastreamQueryKeys.mailing.paged.base() }).then(() => {
        return queryClient.invalidateQueries({
          queryKey: datastreamQueryKeys.mailing.settings.byMailingId(mailingId),
        });
      });

      onSuccess?.();
    },
  });
};
